import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAccessibility, DEFAULT_A11Y, A11ySettings } from "../../context/AccessibilityContext";
import { useAuth } from "../../context/AuthContext";
import { useAuthModal } from "../../context/AuthModalContext";
import { useTranslation } from "react-i18next";

function Sidebar({ active, loggedIn, onLogin }: { active: string; loggedIn: boolean; onLogin: () => void }) {
  const { t } = useTranslation();
  const MENU = [
    { labelKey: "account.settings", path: "/account/settings", icon: "⚙" },
    { labelKey: "account.reports", path: "/account/reports", icon: "📄" },
    { labelKey: "account.accessibility", path: "/account/accessibility", icon: "♿" },
    { labelKey: "account.plans", path: "/account/plans", icon: "💳" },
  ];

  if (!loggedIn) {
    return (
      <div style={{ width: "220px", flexShrink: 0, background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "16px 20px", height: "fit-content" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 0 11px 17px", marginLeft: "-20px", fontSize: "14px", color: "#C9A84C", fontWeight: 700, background: "rgba(201,168,76,0.08)", borderLeft: "3px solid #C9A84C" }}>
          <span>♿</span> {t("account.accessibility")}
        </div>
        <div style={{ borderTop: "1px solid var(--border)", margin: "12px 0" }} />
        <p style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "12px", lineHeight: "1.5" }}>
          Login to access full account settings and sync preferences across devices.
        </p>
        <button onClick={onLogin}
          style={{ background: "#C9A84C", color: "#2C2C2C", border: "none", padding: "8px 16px", borderRadius: "8px", fontWeight: 700, fontSize: "13px", cursor: "pointer", width: "100%" }}>
          Login
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: "220px", flexShrink: 0, background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "16px 0", height: "fit-content" }}>
      {MENU.map(item => (
        <Link key={item.path} to={item.path}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 20px", fontSize: "14px", color: active === item.path ? "#C9A84C" : "var(--text-primary)", fontWeight: active === item.path ? 700 : 400, background: active === item.path ? "rgba(201,168,76,0.08)" : "transparent", borderLeft: active === item.path ? "3px solid #C9A84C" : "3px solid transparent", textDecoration: "none" }}>
          <span>{item.icon}</span> {t(item.labelKey)}
        </Link>
      ))}
      <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }} />
      <button onClick={() => { localStorage.removeItem("token"); window.location.href = "/"; }}
        style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "11px 20px", fontSize: "14px", color: "var(--error)", background: "transparent", border: "none", cursor: "pointer", fontWeight: 500, borderLeft: "3px solid transparent" }}>
        <span>🚪</span> {t("account.logout")}
      </button>
    </div>
  );
}

function Toggle({ value, onChange, label, desc }: { value: boolean; onChange: (v: boolean) => void; label: string; desc: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0", borderBottom: "0.5px solid var(--border)" }}>
      <div>
        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)" }}>{label}</div>
        <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "3px", maxWidth: "380px" }}>{desc}</div>
      </div>
      <button onClick={() => onChange(!value)}
        style={{ width: "48px", height: "26px", borderRadius: "13px", background: value ? "#C9A84C" : "var(--border)", border: "none", cursor: "pointer", position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
        <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "white", position: "absolute", top: "3px", left: value ? "25px" : "3px", transition: "left 0.2s" }}></div>
      </button>
    </div>
  );
}

function RadioGroup({ value, onChange, options, label, desc }: { value: string; onChange: (v: string) => void; options: {value: string; label: string; preview?: string}[]; label: string; desc: string }) {
  return (
    <div style={{ padding: "16px 0", borderBottom: "0.5px solid var(--border)" }}>
      <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "12px" }}>{desc}</div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {options.map(opt => (
          <button key={opt.value} onClick={() => onChange(opt.value)}
            style={{ padding: "8px 16px", borderRadius: "8px", border: value === opt.value ? "2px solid #C9A84C" : "1.5px solid var(--border)", background: value === opt.value ? "rgba(201,168,76,0.1)" : "var(--card-bg)", color: value === opt.value ? "#C9A84C" : "var(--text-primary)", fontSize: "13px", fontWeight: value === opt.value ? 700 : 400, cursor: "pointer", transition: "all 0.15s" }}>
            {opt.preview && <span style={{ marginRight: "6px" }}>{opt.preview}</span>}
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Accessibility() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { openModal } = useAuthModal();
  const { settings, previewSettings, revertSettings, saveSettings } = useAccessibility();
  const location = useLocation();

  const [formValues, setFormValues] = useState<A11ySettings>(settings);
  const [hasChanges, setHasChanges] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync form when saved settings load (e.g. API fetch completes after mount)
  useEffect(() => {
    if (!hasChanges) {
      setFormValues(settings);
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revert visual preview on unmount if there are unsaved changes
  const hasChangesRef = useRef(hasChanges);
  useEffect(() => { hasChangesRef.current = hasChanges; }, [hasChanges]);

  useEffect(() => {
    return () => {
      if (hasChangesRef.current) {
        revertSettings();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key: keyof A11ySettings, value: string | boolean) => {
    const next = { ...formValues, [key]: value };
    setFormValues(next);
    previewSettings(next);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    await saveSettings(formValues);
    setSaving(false);
    setHasChanges(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    setFormValues(DEFAULT_A11Y);
    previewSettings(DEFAULT_A11Y);
    setHasChanges(true);
    setSaved(false);
  };

  return (
    <div style={{ maxWidth: "900px", margin: "0 auto", padding: "32px 24px" }}>
      <h1 style={{ fontSize: "22px", fontWeight: 700, marginBottom: "24px", color: "var(--text-primary)" }}>Account</h1>
      <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
        <Sidebar active={location.pathname} loggedIn={!!user} onLogin={openModal} />
        <div style={{ flex: 1 }}>

          {hasChanges && (
            <div style={{ background: "#FFF8E1", border: "1px solid rgba(201,168,76,0.5)", borderRadius: "8px", padding: "10px 16px", marginBottom: "16px", fontSize: "13px", color: "#7F6000", display: "flex", alignItems: "center", gap: "8px" }}>
              <span>⚠</span>
              <span>You have unsaved changes. Click <strong>Save Settings</strong> to keep them, or navigate away to discard.</span>
            </div>
          )}

          <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "28px 32px", marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "20px", borderBottom: "1px solid var(--border)", marginBottom: "8px" }}>
              <div>
                <h2 style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" }}>{t("accessibility.title")}</h2>
                <p style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "3px" }}>{t("accessibility.subtitle")}</p>
              </div>
              <button onClick={handleReset}
                style={{ fontSize: "12px", color: "var(--text-muted)", background: "transparent", border: "1px solid var(--border)", padding: "6px 12px", borderRadius: "6px", cursor: "pointer" }}>
                {t("accessibility.reset")}
              </button>
            </div>

            <div style={{ marginBottom: "4px", paddingTop: "8px" }}>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>{t("accessibility.vision")}</div>
              <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{t("accessibility.visionDesc")}</div>
            </div>

            <RadioGroup value={formValues.font_size} onChange={v => handleChange("font_size", v)}
              label={t("accessibility.fontSize")}
              desc={t("accessibility.fontSizeDesc")}
              options={[
                { value: "small", label: t("accessibility.small"), preview: "A" },
                { value: "medium", label: t("accessibility.medium"), preview: "A" },
                { value: "large", label: t("accessibility.large"), preview: "A" },
                { value: "xlarge", label: t("accessibility.xlarge"), preview: "A" },
              ]} />

            <Toggle value={formValues.high_contrast} onChange={v => handleChange("high_contrast", v)}
              label={t("accessibility.highContrast")}
              desc={t("accessibility.highContrastDesc")} />

            <RadioGroup value={formValues.colour_blind_mode} onChange={v => handleChange("colour_blind_mode", v)}
              label={t("accessibility.colourBlind")}
              desc={t("accessibility.colourBlindDesc")}
              options={[
                { value: "none", label: t("accessibility.none") },
                { value: "deuteranopia", label: "Deuteranopia", preview: "🟢" },
                { value: "protanopia", label: "Protanopia", preview: "🔴" },
                { value: "tritanopia", label: "Tritanopia", preview: "🔵" },
              ]} />

            <Toggle value={formValues.underline_links} onChange={v => handleChange("underline_links", v)}
              label={t("accessibility.underlineLinks")}
              desc={t("accessibility.underlineLinksDesc")} />

            <div style={{ marginTop: "24px", marginBottom: "4px", paddingTop: "8px", borderTop: "0.5px solid var(--border)" }}>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>{t("accessibility.motion")}</div>
              <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{t("accessibility.motionDesc")}</div>
            </div>

            <Toggle value={formValues.reduce_motion} onChange={v => handleChange("reduce_motion", v)}
              label={t("accessibility.reduceMotion")}
              desc={t("accessibility.reduceMotionDesc")} />

            <div style={{ marginTop: "24px", marginBottom: "4px", paddingTop: "8px", borderTop: "0.5px solid var(--border)" }}>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>{t("accessibility.interaction")}</div>
              <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>{t("accessibility.interactionDesc")}</div>
            </div>

            <Toggle value={formValues.read_aloud} onChange={v => handleChange("read_aloud", v)}
              label={t("accessibility.readAloud")}
              desc={t("accessibility.readAloudDesc")} />

            <Toggle value={formValues.show_hovercards} onChange={v => handleChange("show_hovercards", v)}
              label={t("accessibility.hovercards")}
              desc={t("accessibility.hovercardsDesc")} />

            <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
              <button onClick={handleSave} disabled={saving || !hasChanges}
                style={{ background: "#C9A84C", color: "#2C2C2C", border: "none", padding: "11px 28px", borderRadius: "8px", fontWeight: 700, fontSize: "14px", cursor: hasChanges && !saving ? "pointer" : "not-allowed", opacity: saving || !hasChanges ? 0.5 : 1 }}>
                {saving ? t("accessibility.saving") : t("accessibility.saveSettings")}
              </button>
              {saved && <span style={{ color: "var(--success)", fontSize: "13px", fontWeight: 600 }}>{t("accessibility.settingsSaved")}</span>}
            </div>
          </div>

          <div style={{ background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "24px 32px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h3 style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)", marginBottom: "6px" }}>{t("accessibility.signLanguage")}</h3>
                <p style={{ fontSize: "13px", color: "var(--text-muted)", lineHeight: "1.6", maxWidth: "380px" }}>
                  {t("accessibility.signLanguageDesc")}
                </p>
              </div>
              <span style={{ background: "#EAE6E0", color: "#5C5C5C", fontSize: "11px", fontWeight: 700, padding: "4px 10px", borderRadius: "8px", whiteSpace: "nowrap" }}>{t("accessibility.comingSoon")}</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
